import { useState, useCallback, useMemo } from 'react';
import { TextArea } from 'devextreme-react/text-area';
import { flattenTree, parseFormula, hasCircularReference } from '@calculom/report-designer';
import { useDesignerStore } from '../store';

interface FormulaEditorProps {
  formula: string;
  onChange: (formula: string) => void;
  currentRowId: string;
}

export function FormulaEditor({ formula, onChange, currentRowId }: FormulaEditorProps) {
  const { nodes } = useDesignerStore();
  const [localValue, setLocalValue] = useState(formula);

  const availableRows = useMemo(() => {
    return flattenTree(nodes)
      .filter((n) => n.rowId !== currentRowId)
      .map((n) => ({
        rowId: n.rowId,
        label: n.data.type === 'header' ? n.data.text
          : n.data.type === 'section' ? n.data.name
          : n.data.type === 'subtotal' ? n.data.label
          : n.data.type === 'calculated' ? n.data.label
          : n.data.type === 'grand_total' ? n.data.label
          : n.data.type === 'account' ? n.data.accountCode
          : n.rowId,
      }));
  }, [nodes, currentRowId]);

  const allRowIds = useMemo(() => {
    return new Set(flattenTree(nodes).map((n) => n.rowId));
  }, [nodes]);

  const validation = useMemo(() => {
    if (!localValue) return null;
    return parseFormula(localValue);
  }, [localValue]);

  const unresolvedRefs = useMemo(() => {
    if (!validation || !validation.valid) return [];
    return validation.references
      .filter((ref) => !allRowIds.has(ref.rowId))
      .map((ref) => ref.rowId);
  }, [validation, allRowIds]);

  const circular = useMemo(() => {
    if (!localValue) return false;
    return hasCircularReference(nodes, currentRowId, localValue);
  }, [nodes, currentRowId, localValue]);

  const handleBlur = useCallback(() => {
    if (localValue !== formula) {
      onChange(localValue);
    }
  }, [localValue, formula, onChange]);

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-600">Formula</label>
      <TextArea
        value={localValue}
        onValueChanged={(e) => setLocalValue(e.value ?? '')}
        onFocusOut={handleBlur}
        placeholder="e.g. [10] + [20] or [REV_TOTAL] - [EXP_TOTAL]"
        height={60}
      />
      {validation && !validation.valid && (
        <div className="text-xs text-red-500">
          {validation.errors.join('; ')}
        </div>
      )}
      {circular && (
        <div className="text-xs text-red-500">Circular reference detected</div>
      )}
      {unresolvedRefs.length > 0 && (
        <div className="text-xs text-amber-600">
          Unknown row ID{unresolvedRefs.length > 1 ? 's' : ''}: {unresolvedRefs.map((r) => `[${r}]`).join(', ')}
        </div>
      )}
      {availableRows.length > 0 && (
        <div className="text-xs text-gray-400">
          Available: {availableRows.map((r) => `[${r.rowId}] ${r.label}`).join(', ')}
        </div>
      )}
    </div>
  );
}
